import { getApiBaseUrl, getStoredToken } from "../api/client";

export interface LocalEngineInstallOptions {
  pairingCode?: string;
  /** db = att2000.mdb only (no ADMS/LAN choice dialog). lan/adms/choice = legacy wizard. */
  mode?: "db" | "lan" | "adms" | "choice";
}

/** Single file HR double-clicks (self-elevating .cmd). */
export const GODO_DESKTOP_INSTALL_FILENAME = "GoDo-Install.cmd";

/** @deprecated Use GODO_DESKTOP_INSTALL_FILENAME */
export const GODO_DESKTOP_INSTALL_LAUNCHER = GODO_DESKTOP_INSTALL_FILENAME;

/**
 * One download only: GoDo-Install.cmd — double-click, approve UAC once, wait for success message.
 */
export async function downloadGoDoDesktopInstall(
  _appOrigin: string = window.location.origin,
  options: LocalEngineInstallOptions = {},
): Promise<void> {
  const api = getApiBaseUrl();
  const jwt = getStoredToken();
  if (!api || !jwt) {
    throw new Error("Sign in to GoDo ERP Pro before installing the desktop helper.");
  }

  const mode = options.mode ?? "choice";
  const qs = mode !== "choice" ? `?mode=${encodeURIComponent(mode)}` : "";
  const res = await fetch(`${api.replace(/\/$/, "")}/v1/desktop-helper/install-launcher${qs}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${jwt}`,
      Accept: "application/octet-stream",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    if (/Dear Customer|monthly basic quota|<html/i.test(text)) {
      throw new Error(
        "السيرفر رجّع صفحة خطأ Cloudflare (حصة منتهية). للمحلي: شغّل GoDo-Dev-Local.cmd ثم حمّل GoDo-Install من localhost:5173.",
      );
    }
    throw new Error(text.slice(0, 400) || `Install failed (${res.status})`);
  }

  const cmd = await res.text();
  const trimmed = cmd.replace(/^\uFEFF/, "").trimStart();
  const looksLikeOurLauncher =
    /^@echo off/i.test(trimmed) &&
    /GoDo ERP Pro - Install/i.test(cmd) &&
    /install-script-by-token/i.test(cmd);
  const looksLikeHtmlError =
    /^\s*<(?:!DOCTYPE|html)/i.test(trimmed) ||
    (/Dear Customer|monthly basic quota/i.test(cmd) && !looksLikeOurLauncher);
  if (!looksLikeOurLauncher || looksLikeHtmlError) {
    throw new Error(
      looksLikeHtmlError
        ? "السيرفر رجّع صفحة خطأ (غالباً حصة Cloudflare). للمحلي: GoDo-Dev-Local.cmd ثم localhost:5173."
        : "استجابة التحميل ليست GoDo-Install.cmd صالحة. تأكد أن API المحلي شغّال (8787) ثم أعد التحميل.",
    );
  }
  const blob = new Blob([cmd], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = GODO_DESKTOP_INSTALL_FILENAME;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadGoDoLocalEngineInstaller(
  appOrigin: string,
  options: LocalEngineInstallOptions = {},
) {
  return downloadGoDoDesktopInstall(appOrigin, options);
}
